import React, { Component } from 'react';
import './Game_ItemStats.css';
import GameAPI from './GameAPI';

const COMPARISON_LABELS = {
    better: 'higher',
    worse: 'lower',
    same: 'equal',
};

const STAT_GROUPS = [
    { key: 'implicit', label: 'Implicit' },
    { key: 'affix', label: 'Affixes' },
    { key: 'legacy', label: 'Legacy' },
];

const PERCENT_STAT_NAMES = new Set([
    'critchance',
    'critdamage',
    'dodge',
]);

function normalizeStatName(name) {
    return String(name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function normalizeStatSource(source) {
    const normalized = String(source || 'Legacy').toLowerCase();
    if (normalized === 'implicit' || normalized === 'affix') {
        return normalized;
    }
    return 'legacy';
}

function formatNumber(value) {
    return Number(Number(value).toFixed(2)).toString();
}

function formatUnit(unitSymbol) {
    const unit = String(unitSymbol || '');
    if (!unit) {
        return '';
    }
    if (/^\s/.test(unit) || unit.startsWith('%')) {
        return unit;
    }
    return ` ${unit}`;
}

function formatStatNumber(definition, value) {
    const statName = normalizeStatName(definition?.name);
    if (PERCENT_STAT_NAMES.has(statName)) {
        return {
            number: formatNumber(Number(value) * 100),
            unit: '%',
        };
    }

    if (statName === 'magicfind') {
        return {
            number: formatNumber(value),
            unit: formatUnit(definition?.unitSymbol || 'MF'),
        };
    }

    if (statName === 'weaponattackspeed') {
        return {
            number: formatNumber(value),
            unit: formatUnit(definition?.unitSymbol || 'APM'),
        };
    }

    return {
        number: formatNumber(value),
        unit: formatUnit(definition?.unitSymbol),
    };
}

export function aggregateItemStatEntries(statEntries) {
    return (statEntries || []).reduce((aggregates, entry) => {
        if (!entry?.statId) {
            return aggregates;
        }

        const current = aggregates.get(entry.statId) || {
            statId: entry.statId,
            valueFrom: 0,
            valueTo: 0,
        };
        const valueFrom = Number(entry.valueFrom);
        const valueTo = Number(entry.valueTo);
        current.valueFrom += Number.isFinite(valueFrom) ? valueFrom : 0;
        current.valueTo += Number.isFinite(valueTo) ? valueTo : 0;
        aggregates.set(entry.statId, current);
        return aggregates;
    }, new Map());
}

export const compareItemStatValue = (value, comparisonValue) => {
    const numericValue = Number(value);
    const numericComparison = Number(comparisonValue);
    if (!Number.isFinite(numericValue) || !Number.isFinite(numericComparison)) {
        return null;
    }
    if (numericValue === numericComparison) {
        return 'same';
    }
    return numericValue > numericComparison ? 'better' : 'worse';
};

const renderStatNumber = (definition, value, comparisonValue) => {
    const comparison = compareItemStatValue(value, comparisonValue);
    const { number, unit } = formatStatNumber(definition, value);
    const displayValue = `${number}${unit}`;

    return (
        <>
            <span
                aria-label={comparison ? `${displayValue}, ${COMPARISON_LABELS[comparison]}` : undefined}
                className="item-value__number"
                data-comparison={comparison || undefined}
            >
                {number}
            </span>
            {unit && <span className="item-value__unit">{unit}</span>}
        </>
    );
};

export class GameItemStats extends Component {
    constructor(props) {
        super(props);
        this.state = {
            stats: [],
        };
        this.isMountedFlag = false;
        this.loadRequestId = 0;
    }

    componentDidMount() {
        this.isMountedFlag = true;
        this.loadStats(this.props.stats);
    }

    componentDidUpdate(prevProps) {
        if (this.props.stats !== prevProps.stats) {
            this.loadStats(this.props.stats);
        }
    }

    componentWillUnmount() {
        this.isMountedFlag = false;
        this.loadRequestId += 1;
    }

    loadStats = async (statEntries) => {
        const requestId = this.loadRequestId += 1;
        const loadedStats = await Promise.all((statEntries || []).map(async (entry) => {
            const definition = await GameAPI.fetchStat(entry.statId);
            return definition ? { definition, entry } : null;
        }));

        if (!this.isMountedFlag || requestId !== this.loadRequestId) {
            return;
        }

        this.setState({ stats: loadedStats.filter(Boolean) });
    };

    renderValue = (definition, entry, comparisonEntry) => {
        const valueFrom = entry.valueFrom;
        const valueTo = entry.valueTo;
        const comparisonFrom = comparisonEntry?.valueFrom;
        const comparisonTo = comparisonEntry?.valueTo;

        if (valueFrom === valueTo) {
            return renderStatNumber(
                definition,
                valueFrom,
                comparisonEntry ? comparisonFrom : undefined,
            );
        }

        return (
            <>
                {renderStatNumber(
                    definition,
                    valueFrom,
                    comparisonEntry ? comparisonFrom : undefined,
                )}
                <span aria-hidden="true"> - </span>
                {renderStatNumber(
                    definition,
                    valueTo,
                    comparisonEntry ? comparisonTo : undefined,
                )}
            </>
        );
    };

    render() {
        const comparisonStats = aggregateItemStatEntries(this.props.comparisonStats);
        const currentStats = aggregateItemStatEntries(this.props.stats);
        const statCounts = (this.props.stats || []).reduce((counts, entry) => {
            if (entry?.statId) {
                counts.set(entry.statId, (counts.get(entry.statId) || 0) + 1);
            }
            return counts;
        }, new Map());
        const totalEntries = Array.from(currentStats.values())
            .filter(entry => (statCounts.get(entry.statId) || 0) > 1)
            .map(entry => ({
                definition: this.state.stats.find(candidate => (
                    candidate.entry.statId === entry.statId
                ))?.definition,
                entry,
            }))
            .filter(candidate => candidate.definition);
        return (
            <div className="game-stats">
                {STAT_GROUPS.map((group) => {
                    const entries = this.state.stats.filter(({ entry }) => (
                        normalizeStatSource(entry.source) === group.key
                    ));
                    if (entries.length === 0) {
                        return null;
                    }

                    return (
                        <section
                            aria-label={`${group.label} stats`}
                            className={`item-stat-group item-stat-group--${group.key}`}
                            key={group.key}
                        >
                            <h4 className="item-stat-group__title">{group.label}</h4>
                            {entries.map(({ definition, entry }, index) => {
                                const comparisonEntry = (statCounts.get(entry.statId) || 0) === 1
                                    ? comparisonStats.get(entry.statId)
                                    : undefined;
                                return (
                                    <div
                                        key={`${group.key}-${entry.statId}-${index}`}
                                        className="stat row"
                                    >
                                        <div className="stat-name">{definition.name}: </div>
                                        <span className="item-value">
                                            {this.renderValue(definition, entry, comparisonEntry)}
                                        </span>
                                    </div>
                                );
                            })}
                        </section>
                    );
                })}
                {totalEntries.length > 0 && (
                    <section
                        aria-label="Total stats"
                        className="item-stat-group item-stat-group--total"
                    >
                        <h4 className="item-stat-group__title">Total</h4>
                        {totalEntries.map(({ definition, entry }) => (
                            <div key={`total-${entry.statId}`} className="stat row">
                                <div className="stat-name">{definition.name}: </div>
                                <span className="item-value">
                                    {this.renderValue(
                                        definition,
                                        entry,
                                        comparisonStats.get(entry.statId),
                                    )}
                                </span>
                            </div>
                        ))}
                    </section>
                )}
            </div>
        );
    }
}
